import cloneDeep from 'lodash/cloneDeep'
import type { BankListItem, NetworkTelecom, PaymentBankScheduleItem } from './interface'
import { useSupportedBanksStore } from '~/stores/supported-banks.store'
import { useDepositStore } from '~/stores/deposit.store'
import { useTransferStore } from '~/stores/transfer.store'

import { usePromotion } from '~/composables/promotion/use-promotion'
import { ResponseStatus } from '~composables/common/interface'
import { useApi } from '~/composables/common/use-api'
import { useMainBank } from '~/composables/account/use-main-bank'

import { DEPOSIT_METHOD, MAINTENANCE_KEY, SCHEDULE_KEY } from '~/constants'
import { CRYPTO_API, DEPOSIT_WITHDRAW_API, PAYMENT_CODE_API, TRANSACTION_API, TRANSFER_API } from '~/configs/api'
import { HttpRequestMethod } from '~/configs/enums'

export function useDepositData() {
  const { setPromotion, getPromotionPackages } = usePromotion()
  const banksStore = useSupportedBanksStore()
  const store = useDepositStore()
  const betAccountStore = useTransferStore()
  const { fetchUserMainBanks } = useMainBank()
  const { request } = useApi()
  const isShowMenuLabel = ref(false)
  const config = useRuntimeConfig()

  async function initListAgency(): Promise<void> {
    try {
      const { data: dataResponse, status } = await request(TRANSFER_API.GET_LIST_AGENCY, {
        method: HttpRequestMethod.GET,
        server: false
      })
      if (status === ResponseStatus.OK) {
        betAccountStore.setCreditRate(parseInt(dataResponse?.creditRate))
        betAccountStore.setBetAccounts(dataResponse?.betAccounts.reduce(
          (result: Record<string, string[]>, item: { betAccount: string, agencyType: string }) => {
            const agencyType = item.agencyType.toUpperCase()
            return { ...result, [agencyType]: [...result[agencyType] ?? [], item.betAccount] }
          },
          {}
        ))
      }
    } catch (error) {
      console.error(error)
    }
  }

  async function fetchBankList(): Promise<void> {
    try {
      if (!banksStore.banks.length) {
        const { data, status } = await request(TRANSACTION_API.BANK_LIST, {
          method: 'GET'
        })
        if (status === ResponseStatus.OK && data) {
          const listBanks: BankListItem[] = data
          const banks = processBankList(listBanks)
          banksStore.setBanks(banks)
        }
      }
    } catch { /* empty */ }
  }

  function setStatementBankLabel(bankCode: string): string {
    const bankSchedules = store.bankSchedules
    if (bankSchedules?.length) {
      const newBankSchedules = bankSchedules.find(
        (bank: { bank_code: string }) => bank.bank_code?.toLowerCase() === bankCode?.toLowerCase()
      )
      let timeSchedule = null
      if (newBankSchedules?.timeSchedule?.length) {
        timeSchedule = newBankSchedules.timeSchedule.find(
          (timeSchedule: PaymentBankScheduleItem) => timeSchedule.note === MAINTENANCE_KEY && timeSchedule.status
        )
        if (!timeSchedule) {
          timeSchedule = newBankSchedules.timeSchedule.find(
            (timeSchedule: PaymentBankScheduleItem) => timeSchedule.note === SCHEDULE_KEY && timeSchedule.status
          )
        }
      }
      return timeSchedule?.note ?? ''
    }
    return ''
  }

  async function fetchNetworkTelecoms(): Promise<void> {
    try {
      const { data } = await request(DEPOSIT_WITHDRAW_API.PHONE_CARD_LIST, {
        method: HttpRequestMethod.GET,
        server: false
      })
      if (data?.cardlist) {
        const { cardlist } = data
        const telecoms: NetworkTelecom[] = Object.keys(cardlist).map(key => {
          const cardProvider = cardlist[key]
          return {
            rate: cardProvider.rate,
            status: cardProvider.status,
            providerId: key,
            fee: Math.round((1 - cardProvider.rate) * 10000) / 100,
            providerName: key,
            values: cardProvider.value?.map((price: number) => ({
              value: price,
              label: NumberUtils.formatNumberWithCommas(price)
            })),
            isDisabled: !data?.status || !cardProvider.status
          }
        })
        const statusNetworkTelecom = checkAllDisabled(telecoms)

        store.setNetworkTelecoms(telecoms)
        store.setStatusNetworkTelecom(statusNetworkTelecom)
      }
    } catch { /* empty */ }
  }

  function checkAllDisabled(telecoms: NetworkTelecom[]): boolean {
    for (const telecom of telecoms) {
      if (!telecom.isDisabled) {
        return false
      }
    }
    return true
  }
  async function fetchEwallet(): Promise<void> {
    const storeEwallets = store.ewallet
    if (!storeEwallets.length) {
      try {
        const { data, status } = await request(DEPOSIT_WITHDRAW_API.EWALLET_LIST, {
          method: 'GET',
          server: false
        })
        if (status === ResponseStatus.OK) {
          store.setEwallet(data)
        }
      } catch { /* empty */ }
    }
  }
  async function fetchMomoCode(): Promise<void> {
    const storeMomoCode = store.ewalletCode
    if (!storeMomoCode) {
      try {
        const { data, status } = await request(PAYMENT_CODE_API.MOMO_CODE, {
          method: 'GET',
          server: false
        })
        if (status === ResponseStatus.OK) {
          store.setEwalletCode(data)
        }
      } catch { /* empty */ }
    }
  }
  async function fetchWithdrawBanks(): Promise<void> {
    const storeWithdrawBanks = store.withdrawBanks
    if (!storeWithdrawBanks.length) {
      try {
        const { data, status } = await request(DEPOSIT_WITHDRAW_API.WITHDRAW_BANK_LIST, {
          method: 'GET',
          server: false
        })
        if (status === ResponseStatus.OK) {
          store.setWithdrawBanks(data)
        }
      } catch { /* empty */ }
    }
  }
  async function fetchStatementBanks(): Promise<void> {
    try {
      const { data, status } = await request(DEPOSIT_WITHDRAW_API.BANK_SCHEDULE, {
        method: 'GET',
        server: false
      })
      if (status === ResponseStatus.OK) {
        store.setBankSchedules(data)
      }
    } catch { /* empty */ }
  }
  async function fetchCryptos(): Promise<void> {
    if (store.cryptos?.length) {
      return
    }
    try {
      const { data, status } = await request(CRYPTO_API.CRYPTO_LIST, {
        method: 'GET'
      })
      if (status === ResponseStatus.OK && data) {
        store.setCryptos(data)
      }
    } catch { /* empty */ }
  }
  const isMaintain = (type: string | undefined): boolean => {
    const { ewallet, ewalletCode, cryptos, networkTelecoms, statusNetworkTelecom } = store

    switch (type) {
      case DEPOSIT_METHOD.MOMO:
        return !config.public.IS_DEPOSIT_EWALLET_ENABLED || !ewallet.length || !ewalletCode
      case DEPOSIT_METHOD.CRYPTO:
        return !cryptos.length || config.public.IS_CRYPTO_MAINTAINED
      case DEPOSIT_METHOD.PHONE_CARD:
        return !networkTelecoms?.length || statusNetworkTelecom
      default:
        return false
    }
  }
  function processBankList(listBanks: BankListItem[]): BankListItem[] {
    return cloneDeep(listBanks)
      .map(bank => ({
        ...bank,
        bank_name_text: `${bank.bank_name}`,
        isDisabled: bank.isMaintained
      }))
      .sort((firstBank, secondBank) => compareBanks(firstBank, secondBank))
  }

  function compareBanks(firstBank: { isMaintained?: boolean }, secondBank: { isMaintained?: boolean }): number {
    if (firstBank.isMaintained && !secondBank.isMaintained) {
      return 1
    }
    if ((firstBank.isMaintained && secondBank.isMaintained) ?? (!firstBank.isMaintained && !secondBank.isMaintained)) {
      return 0
    }
    return -1
  }

  function fetchAllDepositData(): void {
    void Promise.all([
      setPromotion(),
      getPromotionPackages(),
      fetchStatementBanks(),
      fetchBankList(),
      fetchUserMainBanks(),
      fetchNetworkTelecoms(),
      fetchEwallet(),
      fetchMomoCode(),
      fetchCryptos()
    ]).then(result => {
      isShowMenuLabel.value = !!result.length
    })
  }

  function fetchAllWithdrawData(): void {
    void Promise.all([fetchWithdrawBanks(), fetchUserMainBanks(), fetchNetworkTelecoms(), fetchBankList()]).then(
      result => {
        isShowMenuLabel.value = !!result.length
      }
    )
  }

  return {
    initListAgency,
    fetchAllWithdrawData,
    fetchAllDepositData,
    setStatementBankLabel,
    isMaintain,
    isShowMenuLabel,
    fetchWithdrawBanks,
    fetchNetworkTelecoms
  }
}
